/**
 * Chat Message Adapters - 核心适配函数
 *
 * 将服务端数据转换为 UI 层 UIMessage 类型。
 * 适配器为纯函数，不包含业务逻辑。
 */

import {
  MessageType,
  SenderRole,
  type UIMessage,
  type UIChoice,
} from '../../types/chat-model'
import type {
  BatchAdapterInput,
  ServerCurrentScene,
  ServerStoryEvent,
  ChoiceActionCreator,
} from './types'

// ============================================================
// 内部辅助函数
// ============================================================

/**
 * 根据事件类型判断发送者角色
 */
function mapEventTypeToRole(eventType?: string): SenderRole {
  switch (eventType) {
    case 'user_choice':
    case 'choice':
      return SenderRole.USER
    case 'assistant':
    case 'scene':
    case 'opening':
    case 'event':
    case 'outcome':
    default:
      return SenderRole.AUTHOR
  }
}

/**
 * 判断内容是否为 JSON 模板
 */
function isJsonTemplate(content?: string): boolean {
  return content?.trim().startsWith('```json') ?? false
}

/**
 * 将服务端选项转换为 UI 选项
 */
function adaptChoicesToUI(
  choices: Array<{ id?: string; text?: string }> | undefined,
  createAction: ChoiceActionCreator
): UIChoice[] {
  if (!choices || choices.length === 0) return []

  return choices
    .filter((c) => c.id && c.text) // 过滤无效选项
    .map((c) => ({
      id: c.id!,
      text: c.text!,
      action: createAction({ id: c.id!, text: c.text! }),
    }))
}

// ============================================================
// 公开适配函数
// ============================================================

/**
 * 将单条服务端故事事件转换为 UIMessage
 * 
 * @param event - 服务端事件
 * @param createChoiceAction - 可选的选择回调创建器，如果提供则会保留 choices
 */
export function adaptStoryEventToUI(
  event: ServerStoryEvent,
  createChoiceAction?: ChoiceActionCreator
): UIMessage {
  const baseMessage: UIMessage = {
    id: event.id || `event_${Date.now()}_${Math.random()}`,
    role: mapEventTypeToRole(event.type),
    type: isJsonTemplate(event.content)
      ? MessageType.JSON_TEMPLATE
      : MessageType.TEXT,
    content: event.content || '',
    timestamp: event.timestamp || new Date().toISOString(),
    isRevealed: true, // 历史消息默认已展示
  }

  // 如果有 choices 且提供了 createChoiceAction，保留 choices
  if (event.choices && event.choices.length > 0 && createChoiceAction) {
    baseMessage.choices = adaptChoicesToUI(event.choices, createChoiceAction)
  }

  return baseMessage
}

/**
 * 将当前场景转换为单条 UIMessage
 * (最新消息，包含选项)
 *
 * @param scene - 服务端当前场景数据
 * @param createChoiceAction - 选择回调创建器，由业务层传入
 * @returns UIMessage，isRevealed 为 false (Galgame 模式需等待播放)
 */
export function adaptSceneToUIMessage(
  scene: ServerCurrentScene,
  createChoiceAction: ChoiceActionCreator
): UIMessage {
  return {
    id: `scene_${Date.now()}`,
    role: SenderRole.AUTHOR,
    type: isJsonTemplate(scene.context)
      ? MessageType.JSON_TEMPLATE
      : MessageType.TEXT,
    content: scene.context || '',
    timestamp: scene.metadata?.time || new Date().toISOString(),
    isRevealed: false, // 最新消息在 Galgame 模式下需等待播放
    choices: adaptChoicesToUI(scene.choices, createChoiceAction),
  }
}

/**
 * 将服务端消息列表（历史 + 当前场景）批量转换为 UIMessage[]
 *
 * 使用场景：
 * - POST /engine-play/new → 初始化会话
 * - GET /engine-play/sessions/{sessionId}/messages → 恢复会话
 * - POST /engine-play/sessions/{sessionId}/restart → 重开会话
 *
 * @param input - 包含 session/storyHistory/currentScene 的输入对象
 * @param createChoiceAction - 选择回调创建器，由业务层传入
 * @returns UIMessage[] 数组
 */
export function adaptServerMessagesToUI(
  input: BatchAdapterInput,
  createChoiceAction: ChoiceActionCreator
): UIMessage[] {
  const messages: UIMessage[] = []

  // 1. 处理历史消息
  // 优先使用 storyHistory，其次尝试从 session 中获取
  const history =
    input.storyHistory ||
    (input.session as { storyHistory?: ServerStoryEvent[] })?.storyHistory ||
    []

  // 处理所有历史消息，保留它们的 choices（如果有）
  for (let i = 0; i < history.length; i++) {
    const event = history[i]
    // 所有历史消息都使用 adaptStoryEventToUI，并传递 createChoiceAction 以保留 choices
    messages.push(adaptStoryEventToUI(event, createChoiceAction))
  }

  // 2. 处理当前场景 (最新消息)
  if (input.currentScene) {
    messages.push(adaptSceneToUIMessage(input.currentScene, createChoiceAction))
  }

  return messages
}

/**
 * 创建用户消息
 * 用于在发送选择时立即显示用户输入
 *
 * @param text - 用户输入的文本
 * @returns UIMessage (role: USER, isRevealed: true)
 */
export function createUserMessage(text: string): UIMessage {
  return {
    id: `user_${Date.now()}`,
    role: SenderRole.USER,
    type: MessageType.TEXT,
    content: text,
    timestamp: new Date().toISOString(),
    isRevealed: true,
  }
}

/**
 * 移除消息列表最后一条消息的 choices
 * 用于在追加新消息前清理旧的选项
 *
 * @param messages - 当前消息列表
 * @returns 新的消息列表 (最后一条消息的 choices 被移除)
 */
export function clearLastMessageChoices(messages: UIMessage[]): UIMessage[] {
  if (messages.length === 0) return messages

  return messages.map((msg, index) => {
    if (index === messages.length - 1 && msg.choices) {
      return { ...msg, choices: undefined }
    }
    return msg
  })
}

/**
 * 将预览响应转换为 UIMessage
 * 用于 POST /engine-play/preview 响应
 *
 * @param previewData - 预览接口响应数据
 * @param createChoiceAction - 选择回调创建器，由业务层传入
 * @returns UIMessage
 */
export function adaptPreviewToUIMessage(
  previewData: { content: string; choices: Array<{ id: string; text: string }> },
  createChoiceAction: ChoiceActionCreator
): UIMessage {
  return {
    id: `preview_${Date.now()}`,
    role: SenderRole.AUTHOR,
    type: MessageType.TEXT,
    content: previewData.content || '',
    timestamp: new Date().toISOString(),
    isRevealed: false, // 预览消息在 Galgame 模式下需等待播放
    choices: adaptChoicesToUI(previewData.choices, createChoiceAction),
  }
}
